import { motion } from "framer-motion";
import { Star, BadgeCheck, Quote } from "lucide-react";

const Stars = ({ rating, testid }) => (
    <div className="flex gap-1" aria-label={`${rating} out of 5 star rating`} data-testid={testid}>
        {[1, 2, 3, 4, 5].map((s) => {
            const fill = Math.min(Math.max(rating - (s - 1), 0), 1);
            return (
                <span key={s} className="relative inline-block h-[15px] w-[15px] shrink-0">
                    <Star size={15} className="absolute inset-0 text-slate-300" />
                    {fill > 0 && (
                        <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
                            <Star size={15} className="fill-amber-400 text-amber-400" />
                        </span>
                    )}
                </span>
            );
        })}
        <span className="ml-1.5 text-xs font-semibold text-slate-500 self-center">{rating.toFixed(1)}</span>
    </div>
);

const TESTIMONIALS = [
    {
        quote: "Brain Brush rebuilt our entire product experience in eight weeks. Conversion doubled within a month of launch — the craft is on another level.",
        name: "Elena Marsh",
        role: "CEO, Urthh",
        rating: 4.5,
        testid: "testimonial-card-urthh",
    },
    {
        quote: "The rare agency that designs like artists and ships like engineers. Our app went from sketch to launch without a single missed milestone.",
        name: "Dr. Arjun Mehta",
        role: "Founder, Torayy",
        rating: 4.0,
        testid: "testimonial-card-torayy",
    },
    {
        quote: "Working across three time zones, communication was flawless. They treat your product like their own — we'd never go back to a big agency.",
        name: "Sofia Lindqvist",
        role: "Head of Product, Mining",
        rating: 5.0,
        testid: "testimonial-card-mining",
    },
];

export const Testimonials = () => {
    return (
        <section id="testimonials" className="relative py-20 lg:py-32 bg-gradient-to-b from-transparent via-pink-50/40 to-transparent">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8 }}
                    className="max-w-2xl"
                >
                    <div className="font-mono-brand text-xs tracking-[0.25em] uppercase font-semibold text-violet-600 mb-4" data-testid="testimonials-eyebrow">
                        Global clients
                    </div>
                    <h2 className="font-display font-extrabold tracking-tight text-slate-900 text-2xl sm:text-3xl lg:text-4xl leading-snug">
                        Trusted across <span className="gradient-text">borders and industries.</span>
                    </h2>
                </motion.div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {TESTIMONIALS.map((t, i) => (
                        <motion.figure
                            key={t.testid}
                            data-testid={t.testid}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.7, delay: i * 0.12 }}
                            className="relative rounded-[1.5rem] border border-white/60 bg-white/80 backdrop-blur-xl p-8 shadow-lg shadow-slate-200/40 hover:shadow-xl hover:shadow-pink-500/15 hover:-translate-y-1.5 transition-all duration-500"
                        >
                            <Quote size={28} className="text-pink-400/60" />
                            <div className="mt-4">
                                <Stars rating={t.rating} testid={`${t.testid}-rating`} />
                            </div>
                            <blockquote className="mt-4 text-sm sm:text-base text-slate-700 leading-relaxed">“{t.quote}”</blockquote>
                            <figcaption className="mt-6 flex items-center gap-3">
                                <div className="h-11 w-11 rounded-full gradient-bg flex items-center justify-center text-white font-display font-bold text-sm">
                                    {t.name.split(" ").map((w) => w[0]).join("")}
                                </div>
                                <div>
                                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                                        {t.name}
                                        <BadgeCheck size={15} className="text-blue-500" />
                                    </div>
                                    <div className="text-xs text-slate-500">{t.role}</div>
                                </div>
                            </figcaption>
                        </motion.figure>
                    ))}
                </div>
            </div>
        </section>
    );
};
